/**
 * <cart-fly>: add-to-cart feedback that keeps the shopper on the page (settings.cart_add_feedback = 'fly').
 * On theme:cart:added a small capsule travels in an arc from the button to the header cart icon, the icon
 * gives one short pulse and the button shows its "added" state. Reduced motion: no travel, state change only.
 * The cart count itself is updated by the header on theme:cart:update; screen readers get the live-region
 * announcement from the add component.
 */
import { define, EVENTS } from './theme.js';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class CartFly extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    document.addEventListener(EVENTS.cartAdded, (event) => this.play(event.detail?.trigger), { signal: this.controller.signal });
  }

  disconnectedCallback() { this.controller?.abort(); }

  play(trigger) {
    const icon = document.querySelector('.header__cart');
    if (trigger) this.markAdded(trigger);
    if (!icon) return;
    if (reduceMotion.matches || !trigger) { this.pulse(icon); return; }

    const from = trigger.getBoundingClientRect(), to = icon.getBoundingClientRect();
    const x0 = from.left + from.width / 2, y0 = from.top + from.height / 2;
    const x1 = to.left + to.width / 2, y1 = to.top + to.height / 2;
    const lift = Math.min(160, Math.max(60, Math.abs(y0 - y1) * 0.35));
    const mx = x0 + (x1 - x0) * 0.45, my = Math.min(y0, y1) - lift;

    const dot = document.createElement('span');
    dot.className = 'cart-fly__dot';
    dot.setAttribute('aria-hidden', 'true');
    document.body.append(dot);
    const at = (x, y, s) => `translate(${x}px, ${y}px) translate(-50%, -50%) scale(${s})`;
    const flight = dot.animate([
      { transform: at(x0, y0, 0.6), opacity: 0 },
      { transform: at(x0, y0 - 10, 1.15), opacity: 1, offset: 0.12 },
      { transform: at(mx, my, 1), offset: 0.5 },
      { transform: at(x1, y1, 0.45), opacity: 0.9 }
    ], { duration: 720, easing: 'cubic-bezier(.45, 0, .25, 1)', fill: 'forwards' });
    flight.finished.then(() => { dot.remove(); this.pulse(icon); }, () => dot.remove());
  }

  pulse(icon) {
    icon.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.16)' }, { transform: 'scale(1)' }],
      { duration: reduceMotion.matches ? 1 : 320, easing: 'cubic-bezier(.3, .7, .4, 1.4)' });
  }

  markAdded(button) {
    const label = button.closest('[data-added-label]')?.dataset.addedLabel;
    clearTimeout(button._addedTimer);
    if (label && !button.dataset.originalLabel) { button.dataset.originalLabel = button.textContent.trim(); button.textContent = label; }
    button.classList.add('button--added');
    button._addedTimer = setTimeout(() => {
      button.classList.remove('button--added');
      if (button.dataset.originalLabel) { button.textContent = button.dataset.originalLabel; delete button.dataset.originalLabel; }
    }, 1800);
  }
}

define('cart-fly', CartFly);
